/* ==========================================================================
   ZELICAT THEME — hero-slideshow.js
   Hero slideshow: autoplay with progress bars (timing via --hero-ms), pause on
   hover / keyboard focus / hidden tab, prev/next, swipe, arrow keys,
   reduced-motion. Settings arrive through data-* attributes on [data-hero].
   ========================================================================== */
(() => {
  'use strict';
  const ZC = window.ZC;
  if (!ZC) return;
  const { $, $$, reduceMotion } = ZC;

  function initHero(hero) {
    const slides = $$('[data-slide]', hero);
    const bars = $$('[data-bar]', hero);
    const count = $('[data-count]', hero);
    const toggle = $('[data-hero-toggle]', hero);
    const nextBtn = $('[data-hero-next]', hero);
    const prevBtn = $('[data-hero-prev]', hero);
    const single = slides.length < 2 || !toggle || !bars.length;

    const DURATION = parseInt(hero.getAttribute('data-speed'), 10) || 7000;   // autoplay speed (ms), set in the theme editor
    const autoplay = hero.getAttribute('data-autoplay') !== 'false';
    const labelPlay = hero.getAttribute('data-label-play') || 'Play slideshow';
    const labelPause = hero.getAttribute('data-label-pause') || 'Pause slideshow';
    hero.style.setProperty('--hero-ms', DURATION + 'ms');

    let index = 0;
    let timer = null;
    let userPaused = reduceMotion || !autoplay;   // no autoplay when the visitor prefers reduced motion
    let focusPaused = false;
    const canHover = window.matchMedia('(hover: hover)');
    const isHover = () => canHover.matches && hero.matches(':hover');   // live check: mouseleave is unreliable when the DOM under the pointer changes

    const setPausedClass = () => hero.classList.toggle('is-paused', userPaused || isHover() || focusPaused);

    function go(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        const active = n === index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
        $$('a, button', s).forEach((el) => { el.tabIndex = active ? 0 : -1; });
      });
      bars.forEach((b, n) => {
        b.classList.remove('is-active', 'is-done');
        b.removeAttribute('aria-current');
        if (n < index) b.classList.add('is-done');
      });
      void hero.offsetWidth;               // restart the progress animation
      if (bars[index]) {
        bars[index].classList.add('is-active');
        bars[index].setAttribute('aria-current', 'true');
      }
      if (count) count.textContent = String(index + 1).padStart(2, '0');
      schedule();
    }

    function schedule() {
      clearTimeout(timer);
      if (!hero.isConnected) return;       // section was re-rendered in the theme editor
      if (single || userPaused || focusPaused || document.hidden) return;
      timer = setTimeout(() => { if (isHover()) { schedule(); return; } go(index + 1); }, DURATION);
    }

    function paintToggle() {
      if (!toggle) return;
      toggle.setAttribute('aria-label', userPaused ? labelPlay : labelPause);
      const pauseIcon = $('[data-icon-pause]', toggle);
      const playIcon = $('[data-icon-play]', toggle);
      if (pauseIcon) pauseIcon.hidden = userPaused;
      if (playIcon) playIcon.hidden = !userPaused;
    }

    function setUserPaused(v) {
      userPaused = v;
      paintToggle();
      setPausedClass();
      go(index);
    }

    const onVisibility = () => {
      if (!hero.isConnected) { document.removeEventListener('visibilitychange', onVisibility); clearTimeout(timer); return; }
      schedule();
    };

    if (!single) {
      if (nextBtn) nextBtn.addEventListener('click', () => go(index + 1));
      if (prevBtn) prevBtn.addEventListener('click', () => go(index - 1));
      bars.forEach((b, n) => b.addEventListener('click', () => go(n)));
      toggle.addEventListener('click', () => setUserPaused(!userPaused));
      hero.addEventListener('mouseenter', setPausedClass);
      hero.addEventListener('mouseleave', () => { setPausedClass(); go(index); });
      // pause while keyboard focus is inside (mouse clicks on the controls must not freeze autoplay)
      hero.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) { focusPaused = true; setPausedClass(); clearTimeout(timer); } });
      hero.addEventListener('focusout', () => { focusPaused = false; setPausedClass(); schedule(); });
      document.addEventListener('visibilitychange', onVisibility);
      hero.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') go(index + 1);
        if (e.key === 'ArrowLeft') go(index - 1);
      });

      // swipe (touch)
      let x0 = null;
      hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      hero.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
        x0 = null;
      }, { passive: true });

      // theme editor: selecting a slide block shows that slide and stops autoplay
      hero.addEventListener('shopify:block:select', (e) => {
        const n = slides.indexOf(e.target.closest('[data-slide]'));
        if (n < 0) return;
        userPaused = true;
        paintToggle();
        setPausedClass();
        go(n);
      });
    }

    if (userPaused) { paintToggle(); setPausedClass(); }
    go(0);
  }

  ZC.mount((root) => {
    $$('[data-hero]', root).forEach((hero) => {
      if (ZC.once(hero, 'hero')) initHero(hero);
    });
  });
})();
